import { useState } from 'react';
import { CheckCircle, Circle, Send, MessageCircle, Lightbulb, AlertTriangle } from 'lucide-react';
import { Anotacao } from './AnnotationPin';

interface AnnotationThreadProps {
  anotacao: Anotacao;
  onReply: (anotacaoId: string, texto: string) => Promise<void>;
  onResolve: (anotacaoId: string) => Promise<void>;
}

const tipoLabels: Record<string, { label: string; icon: typeof MessageCircle; color: string }> = {
  comentario: { label: 'Comentario', icon: MessageCircle, color: 'text-blue-600' },
  sugestao: { label: 'Sugestao', icon: Lightbulb, color: 'text-amber-600' },
  problema: { label: 'Problema', icon: AlertTriangle, color: 'text-red-600' },
};

export function AnnotationThread({ anotacao, onReply, onResolve }: AnnotationThreadProps) {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const tipoConfig = tipoLabels[anotacao.tipo] || tipoLabels.comentario;
  const TipoIcon = tipoConfig.icon;

  const handleReply = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    try {
      await onReply(anotacao.id, replyText.trim());
      setReplyText('');
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    await onResolve(anotacao.id);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${anotacao.resolvido ? 'border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-900/10' : 'border-gray-200 dark:border-gray-700'}`}>
      {/* Header */}
      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <TipoIcon className={`w-4 h-4 flex-shrink-0 ${tipoConfig.color}`} />
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Pag. {anotacao.pagina}
          </span>
          <span className="text-xs text-gray-400">|</span>
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {tipoConfig.label}
          </span>
        </div>
        <button
          onClick={handleResolve}
          className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded transition-colors ${
            anotacao.resolvido
              ? 'text-green-600 bg-green-100 hover:bg-green-200 dark:bg-green-900/30'
              : 'text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
          }`}
          title={anotacao.resolvido ? 'Reabrir' : 'Resolver'}
        >
          {anotacao.resolvido ? (
            <CheckCircle className="w-3.5 h-3.5" />
          ) : (
            <Circle className="w-3.5 h-3.5" />
          )}
          {anotacao.resolvido ? 'Resolvido' : 'Resolver'}
        </button>
      </div>

      {/* Mensagem principal */}
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-gray-900 dark:text-white">{anotacao.user.nome}</span>
          <span className="text-xs text-gray-400">{formatDate(anotacao.createdAt)}</span>
          {anotacao.user.role === 'admin' && (
            <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded">
              Equipe
            </span>
          )}
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{anotacao.texto}</p>
      </div>

      {/* Respostas */}
      {anotacao.respostas.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          {anotacao.respostas.map(resp => (
            <div key={resp.id} className="px-3 py-2 border-b border-gray-50 dark:border-gray-800 last:border-b-0 ml-3 border-l-2 border-l-gray-200 dark:border-l-gray-600">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{resp.user.nome}</span>
                <span className="text-[10px] text-gray-400">{formatDate(resp.createdAt)}</span>
                {resp.user.role === 'admin' && (
                  <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded">
                    Equipe
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{resp.texto}</p>
            </div>
          ))}
        </div>
      )}

      {/* Reply form */}
      {!anotacao.resolvido && (
        <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 flex gap-2">
          <input
            type="text"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleReply()}
            placeholder="Responder..."
            className="flex-1 text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={handleReply}
            disabled={!replyText.trim() || sending}
            className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
